import React, { useState } from 'react'
import moment from 'moment'
import dayjs from 'dayjs'
import { Space, DatePicker, Input } from 'antd'
import { SearchOutlined, FilterOutlined, PrinterOutlined } from '@ant-design/icons'
import Button from '../../components/common/button/button'
import InputComponent from '../../components/common/input/input'
import { fetchClearanceReports } from '../../api/clearanceLabelApiService/clearanceLabelApiUtils'

const { RangePicker } = DatePicker

export const formatDate = (text) => {
  if (text) {
    const dateObject = moment(text).local()
    const formattedDate = dateObject.format('DD/MM/YYYY')
    const formattedTime = dateObject.format('hh:mm A')
    return `${formattedDate} ${formattedTime}`
  }
  return ''
}

// Filter utility functions
export const getColumnSearchProps = (dataIndex, title) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
      <Input
        placeholder={`Search ${title}`}
        value={selectedKeys[0]}
        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm({ closeDropdown: true })}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="primary"
          onClick={() => confirm({ closeDropdown: true })}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (clearFilters) clearFilters()
            setSelectedKeys([])
            confirm({ closeDropdown: true, clearFilters: true })
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    </div>
  ),
  filterIcon: filtered => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    if (!value || value === '') return true
    if (record[dataIndex] === null || record[dataIndex] === undefined) return false
    return record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
  }
})

export const getDateFilterProps = () => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
      <RangePicker
        value={selectedKeys[0]}
        onChange={(dates) => {
          setSelectedKeys(dates ? [dates] : [])
        }}
        style={{ marginBottom: 8, display: 'block', width: '100%' }}
        format="DD/MM/YYYY"
        placeholder={['Start Date', 'End Date']}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="primary"
          onClick={() => confirm({ closeDropdown: true })}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (clearFilters) clearFilters()
            setSelectedKeys([])
            confirm({ closeDropdown: true, clearFilters: true })
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    </div>
  ),
  filterIcon: (filtered) => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    if (!value || !value[0] || !value[1]) {
      return true
    }

    const startDate = value[0].startOf('day')
    const endDate = value[1].endOf('day')
    const recordDate = dayjs(record.createdAt)

    return (
      (recordDate.isAfter(startDate) || recordDate.isSame(startDate)) &&
      (recordDate.isBefore(endDate) || recordDate.isSame(endDate))
    )
  }
})

export const getCustomFilterWithSearch = (dataIndex, options) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
    const [searchText, setSearchText] = useState('')
    const filteredOptions = options.filter(option =>
      option.toString().toLowerCase().includes(searchText.toLowerCase())
    )

    return (
      <div style={{ padding: 16, background: '#fff', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', width: 240 }} onClick={(e) => e.stopPropagation()}>
        <Input
          placeholder={`Search ${dataIndex}...`}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ marginBottom: 8 }}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        />
        <div style={{ maxHeight: '200px', overflow: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {filteredOptions.map((option, index) => (
              <div key={option || `empty-${index}`} style={{ margin: '8px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedKeys.includes(option)}
                    onChange={e => {
                      const newSelectedKeys = e.target.checked
                        ? [...selectedKeys, option]
                        : selectedKeys.filter(key => key !== option)
                      setSelectedKeys(newSelectedKeys)
                    }}
                    style={{ marginRight: 8 }}
                  />
                  <span>{option || '(Empty)'}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
          <Button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (clearFilters) clearFilters()
              setSelectedKeys([])
              setSearchText('')
              confirm({ closeDropdown: true, clearFilters: true })
            }}
            size="small"
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() => confirm({ closeDropdown: true })}
            size="small"
          >
            OK
          </Button>
        </div>
      </div>
    )
  },
  filterIcon: (filtered) => (
    <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    if (!value) return true
    if (record[dataIndex] == null) return value === null || value === ''
    return record[dataIndex].toString() === value.toString()
  }
})

// Handle saving edits to clearance labels
export const handleSaveEdit = async (formEditRow, editRowData, setLoading, setIsReloading, instance, setEditModalVisible,
  messageApi, fetchData) => {
  try {
    await formEditRow.validateFields()

    if (!editRowData.guid) {
      return false
    }

    setLoading(true)
    setIsReloading(true)

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300))

    setEditModalVisible(false)
    messageApi.success('Clearance labels updated!')
    await fetchData()
    return true
  } catch {
    messageApi.error('Failed to update clearance labels')
    return false
  } finally {
    setLoading(false)
    setIsReloading(false)
  }
}

// Validate edit changes
export const validateEditChange = (field, value, messageApi) => {
  if (field === 'quantity') {
    const updatedValue = parseInt(value, 10)
    if (isNaN(updatedValue) || updatedValue < 1) {
      messageApi.error('Quantity cannot be less than 1!')
      return false
    }
  }

  if (field === 'wasPrice') {
    const updatedValue = parseFloat(value)
    if (isNaN(updatedValue) || updatedValue < 0) {
      messageApi.error('Was Price cannot be negative!')
      return false
    }
  }

  if (field === 'currentPrice') {
    const updatedValue = parseFloat(value)
    if (isNaN(updatedValue) || updatedValue < 0) {
      messageApi.error('Current Price cannot be negative!')
      return false
    }
  }

  if (field === 'apn' && !value) {
    messageApi.error('APN cannot be empty!')
    return false
  }

  return true
}

// Get table columns for the main clearance labels table
export const getClearanceLabelsColumns = (handleMainEdit) => {
  return [
    {
      title: 'Label Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => {
        if (!text) return ''
        const isoMatch = text.match(/(\d{4}-\d{2}-\d{2}T[\d:.Z]+)$/)
        if (isoMatch) {
          const prefix = text.slice(0, text.length - isoMatch[0].length)
          return prefix + formatDate(isoMatch[0])
        }
        return text
      }
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      ellipsis: true
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => formatDate(text)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<PrinterOutlined />}
            onClick={() => handleMainEdit(record)}
            type="text"
            tooltip="Print"
          />
        </Space>
      )
    }
  ]
}

// Get edit columns for the edit modal with was price and current price
export const getEditColumns = (handleEditTableChange) => {
  return [
    {
      title: 'APN',
      dataIndex: 'apn',
      key: 'apn',
      width: 100,
      render: (text) => <span>{text}</span>
    },
    {
      title: 'Was Price',
      dataIndex: 'wasPrice',
      key: 'wasPrice',
      width: 120,
      render: (text) => `₱${parseFloat(text || 0).toFixed(0)}`
    },
    {
      title: 'Current Price',
      dataIndex: 'currentPrice',
      key: 'currentPrice',
      width: 120,
      render: (text) => `₱${parseFloat(text || 0).toFixed(0)}`
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (text, record) => (
        <InputComponent
          type="number"
          min={1}
          value={text}
          onChange={(e) => handleEditTableChange(e.target.value, record, 'quantity')}
        />
      )
    }
  ]
}

// Fetch data utility function using real API
export const fetchDataUtil = async (instance, setRows, setIsReloading, messageApi) => {
  try {
    await fetchClearanceReports(instance, setRows, setIsReloading)
  } catch {
    messageApi.error('Failed to load clearance labels')
    setIsReloading(false)
  }
}

// Handle deleting selected clearance items
export const handleDeleteClearanceItems = async (
  selectedRows,
  setLoading,
  setIsReloading,
  instance,
  setRows,
  setSelectedRows,
  setDeleteModalVisible,
  messageApi
) => {
  try {
    setLoading(true)
    setIsReloading(true)

    const idsToDelete = selectedRows.map((item) => item._id)

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300))

    // Update component state
    setRows(prevRows => prevRows.filter(row => !idsToDelete.includes(row._id)))
    setSelectedRows([])
    setDeleteModalVisible(false)
    messageApi.success('Deleted successfully!')
  } catch {
    messageApi.error('An unexpected error occurred during deletion')
  } finally {
    setLoading(false)
    setIsReloading(false)
  }
}
